import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "../components/layout/PageLayout";
import { useUser } from "../Context/UserContext";
import {
  User,
  Pencil,
  PlusCircle,
  ListChecks,
  ClipboardList,
  Search,
  BookOpen,
  Building2,
  LogOut,
  Star,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { cn } from "../lib/utils";

const MenuCard = ({ icon: Icon, title, description, to, onClick, className }) => {
  const content = (
    <Card className={cn("h-full transition-colors hover:bg-muted", className)}>
      <CardContent className="flex items-start gap-3 pt-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );

  if (to) {
    return (
      <Link to={to} className="block">
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className="block w-full text-left">
      {content}
    </button>
  );
};

const Menu = () => {
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleEditProfile = () => {
    navigate(
      `/profile/${user?.user?.id_user}/${user?.user?.nombre}-${user?.user?.apellido}`,
      { state: { isEditing: true } }
    );
  };

  const handleAddCompany = () => {
    navigate(
      `/profile/${user?.user?.id_user}/${user?.user?.nombre}-${user?.user?.apellido}`,
      { state: { isModalOpen: true } }
    );
  };

  const handleEditCompany = () => {
    navigate(
      `/company-profile/${user?.companyUser?.id_company}/admin/identificacion`
    );
  };

  const handleLogout = () => {
    logout(); // Llamada a la función de logout
    navigate("/"); // Redirigir a la página principal
  };

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const licitacionesItems = [
    {
      icon: Search,
      title: "Buscar licitaciones",
      description: "Explora licitaciones publicadas por otros.",
      to: "/licitaciones",
    },
    {
      icon: PlusCircle,
      title: "Crear una licitación",
      description: "Publica una nueva licitación ¡Gratis!",
      to: "/create-tender-step-1",
    },
    {
      icon: ListChecks,
      title: "Mis licitaciones",
      description: "Gestiona tus licitaciones existentes.",
      to: "/mis-licitaciones",
    },
    {
      icon: ClipboardList,
      title: "Mis Postulaciones",
      description: "Gestiona las postulaciones que has realizado.",
      to: "/mis-postulaciones",
    },
    {
      icon: Star,
      title: "Mis proveedores favoritos",
      description: "Accede rápido a los proveedores que guardaste.",
      to: "/favoritos",
    },
  ];

  const personalItems = [
    {
      icon: User,
      title: "Accede a tu perfil",
      description: "Tu perfil es creado automáticamente, ¡Personalízalo!",
      to: `/profile/${user?.user?.id_user}/${user?.user?.nombre}-${user?.user?.apellido}`,
    },
    {
      icon: Pencil,
      title: "Editar tu perfil",
      description: "Actualiza tu información personal y comparte tu experiencia.",
      onClick: handleEditProfile,
    },
    {
      icon: LogOut,
      title: "Cerrar sesión",
      description: "Finaliza tu sesión actual de forma segura.",
      onClick: handleLogout,
    },
  ];

  const empresaItems = user?.companyUser
    ? [
        {
          icon: Building2,
          title: "Ver perfil de la empresa",
          description: "Revisa la información de tu empresa.",
          to: `/company-profile/${user?.companyUser?.id_company}/${user?.companyUser?.nombre_empresa}`,
        },
        {
          icon: Pencil,
          title: "Editar perfil de la empresa",
          description: "Actualiza la información de tu empresa.",
          onClick: handleEditCompany,
        },
        {
          icon: BookOpen,
          title: "Navegar catálogo",
          description: "Encuentra proveedores registrados en nuestro catálogo.",
          to: "/catalogo",
        },
      ]
    : [
        {
          icon: PlusCircle,
          title: "Registrar empresa",
          description: "Crea un perfil para tu empresa gratis.",
          onClick: handleAddCompany,
        },
      ];

  return (
    <PageLayout>
      <div className="mx-auto max-w-6xl space-y-10 px-4 py-8 sm:px-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Accesos Rápidos</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MenuCard icon={Search} title="Explora Licitaciones" description="Encuentra oportunidades de negocio." to="/licitaciones" />
            <MenuCard icon={BookOpen} title="Explora Catálogo" description="Conéctate con proveedores confiables." to="/catalogo" />
            {user?.companyUser ? (
              <MenuCard
                icon={Building2}
                title="Mi Empresa"
                description="Accede al perfil de tu empresa."
                to={`/company-profile/${user.companyUser.id_company}/${user.companyUser.nombre_empresa}`}
              />
            ) : (
              <MenuCard
                icon={PlusCircle}
                title="Registrar Empresa"
                description="Crea un perfil para tu empresa."
                onClick={handleAddCompany}
              />
            )}
            <MenuCard
              icon={User}
              title="Mi Perfil"
              description="Administra tu información personal."
              to={`/profile/${user?.user?.id_user}/${user?.user?.nombre}-${user?.user?.apellido}`}
            />
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-foreground">Sección Licitaciones</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {licitacionesItems.map((item) => (
              <MenuCard key={item.title} {...item} />
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-foreground">Sección Personal</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {personalItems.map((item) => (
              <MenuCard key={item.title} {...item} />
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-foreground">Sección Empresa</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {empresaItems.map((item) => (
              <MenuCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Menu;
